import { Card, Text, useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { CardActionArea, CardContent, Stack } from "@mui/material";
import { useWorkerProfilePicture } from "@src/appV2/Agents/api/useWorkerProfilePicture";
import { type WorkplaceCommentType } from "@src/appV2/redesign/Workplace/Review/api/useWorkplaceComments";
import { parseISO } from "date-fns";

import { Reaction } from "../types";
import { WorkplaceReviewCommentAuthorInfo } from "./CommentAuthorInfo";
import { ConfirmAnonymizeCommentBottomSheet } from "./ConfirmAnonymizeCommentBottomSheet";
import { ConfirmRemoveCommentBottomSheet } from "./ConfirmRemoveCommentBottomSheet";
import { WorkplaceReviewCommentLikesButton } from "./LikesButton";
import { WorkplaceReviewCommentRepliesAndLikesWrapper } from "./RepliesAndLikesWrapper";
import { WorkplaceReviewCommentRepliesCountIcon } from "./RepliesCountIcon";
import { WorkplaceReviewAnonymizeIcon } from "./WorkplaceReviewAnonymizeIcon";
import { WorkplaceReviewRemoveIcon } from "./WorkplaceReviewRemoveIcon";

interface WorkplaceReviewCommentCardProps {
  comment: WorkplaceCommentType;
  workplaceId: string;
  viewerIsAuthor: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onLikeChanged: (comment: WorkplaceCommentType) => void;
  onAnonymized: (comment: WorkplaceCommentType) => void;
  onRemoved: (commentId: string) => void;
}

export function WorkplaceReviewCommentCard(props: WorkplaceReviewCommentCardProps) {
  const { comment, workplaceId, onClick, onLikeChanged, viewerIsAuthor, onAnonymized, onRemoved } =
    props;
  const { createdAt, workerInfo, text, aggregates, workerReaction } = comment.attributes;
  const { id: workerId, name: workerName } = workerInfo;
  const { totalLikes, totalReplies } = aggregates;

  const anonModalState = useModalState();
  const removeModalState = useModalState();
  const { data: profilePicture, isInitialLoading: isProfilePictureLoading } =
    useWorkerProfilePicture(String(workerId), {
      enabled: isDefined(workerId),
    });

  return (
    <>
      <Card outlined variant="tertiary">
        <CardActionArea onClick={onClick}>
          <CardContent
            sx={{
              padding: 7,
              height: "100%",
              minHeight: "11rem",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <Text variant="body2" sx={{ paddingBottom: 6 }}>
              {text}
            </Text>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <WorkplaceReviewCommentAuthorInfo
                workerName={workerName}
                profilePictureUrl={profilePicture?.distribution_url}
                isLoadingProfilePicture={isProfilePictureLoading}
                createdAt={parseISO(createdAt)}
              />

              <WorkplaceReviewCommentRepliesAndLikesWrapper>
                {viewerIsAuthor && (
                  <WorkplaceReviewAnonymizeIcon
                    onAnonymize={() => {
                      anonModalState.openModal();
                    }}
                  />
                )}

                <WorkplaceReviewCommentRepliesCountIcon count={totalReplies} />

                <WorkplaceReviewCommentLikesButton
                  workplaceId={workplaceId}
                  commentId={comment.id}
                  isLiked={workerReaction === Reaction.LIKE}
                  count={totalLikes}
                  onLikeChanged={(reaction) => {
                    onLikeChanged(reaction);
                  }}
                />

                {viewerIsAuthor && (
                  <WorkplaceReviewRemoveIcon
                    onRemove={() => {
                      removeModalState.openModal();
                    }}
                  />
                )}
              </WorkplaceReviewCommentRepliesAndLikesWrapper>
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
      {viewerIsAuthor && (
        <ConfirmAnonymizeCommentBottomSheet
          modalState={anonModalState}
          commentId={comment.id}
          workplaceId={workplaceId}
          onAnonymized={onAnonymized}
        />
      )}
      {viewerIsAuthor && (
        <ConfirmRemoveCommentBottomSheet
          modalState={removeModalState}
          commentId={comment.id}
          workplaceId={workplaceId}
          onRemoved={() => {
            onRemoved(comment.id);
          }}
        />
      )}
    </>
  );
}
